<template>
    <div class="hotel-tourist-form">
        <tourist-form
            :ref="el => formRefs.push(el)"
            v-model="tourist"
            :index="index"
            :disabled="disabled"
            :corporate-policy-violation="corporatePolicyViolation"
            :hide-index="hideIndex"
            :disable-private-client-name="lockEditPrivateClientName"
            :hide-contact-data="roomLength !== 1 && !isTourLead && sameContactDataForAll"
            :selected-tourists="selectedTourists"
            :disable-citizenship="disableCitizenship"
            :start-date="startDate"
            :country-id="countryId"
            :disable-required-rule="disableRequiredRule"
            @person-selected="personSelected"
        >
            <template v-slot:label>
                <div class="hotel-tourist-form__label d-flex">
                    <div
                        v-if="!hideIndex"
                        class="hotel-tourist-form__subtitle text-subtitle-1 text-capitalize font-weight-bold"
                    >
                        {{ `${$t('guest', 1)} ${index + 1} ${isChild ? `(${$t('child')})` : ''}` }}
                    </div>
                    <div v-if="bedType" class="hotel-tourist-form__bed-type ms-4 d-flex align-center">
                        <div v-if="bedType === 'onExtraBed'" class="font-weight-bold text-grey">
                            <v-icon color="info" start>mdi-bed-outline</v-icon>
                            {{ $t('extra_bed') }}
                        </div>
                        <div v-if="bedType === 'onWithoutPlace'" class="font-weight-bold text-grey">
                            <v-icon color="warning" start>mdi-bed-empty</v-icon>
                            {{ $t('without_bed') }}
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:beforeContacts>
                <v-col v-if="showMealTypes" cols="12" md="4" lg="2" class="hideEmpty">
                    <meal-type-selector
                        v-model="alternativeMealTypeId"
                        :room-index="roomIndex"
                        :standard-meal-name="standardMealName"
                        :disabled="disabled"
                        :is-tour-lead="isTourLead"
                        :tourist="tourist"
                        data-cy="tourist-field-meal-type"
                    />
                </v-col>
            </template>
            <v-col v-if="showSameContactDataCheckbox" cols="12">
                <v-checkbox
                    :model-value="sameContactDataForAll"
                    :label="`${$t('same_contact_data')}`"
                    @update:model-value="changeSameContactData"
                />
            </v-col>
        </tourist-form>
        <hotel-tourist-passport-form
            :ref="el => formRefs.push(el)"
            :room-index="roomIndex"
            :index="index"
            :disabled="disabled"
            :corporate-policy-violation="corporatePolicyViolation"
            :disable-required-rule="disableRequiredRule"
            @set-prop="$emit('change-passport')"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import MealTypeSelector from '@/components/booking/forms/fields/MealTypeSelector.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import HotelTouristPassportForm from '@/components/hotels/booking/form/HotelTouristPassportForm.vue'
    import {EventBus, SAME_CONTACT_DATA_FOR_ALL} from '@/utils/event-bus'
    import {BookingFieldsMixin} from '@/components/booking/mixins/BookingFieldsMixin.vue'
    import TouristForm from '~src/components/booking/forms/touristForm.src'

    @Component({
        components: {
            TouristForm,
            HotelTouristPassportForm,
            MealTypeSelector,
        },
    })
    export class HotelTouristForm extends mixins(FormValidationRulesMixin, BookingFieldsMixin) {
        @VModel() tourist
        @Prop() roomIndex
        @Prop() index
        @Prop() disabled
        @Prop({default: false}) showMealTypes
        @Prop() standardMealName
        @Prop() corporatePolicyViolation
        @Prop({default: false, type: Boolean}) hideIndex
        @Prop({required: true}) startDate
        @Prop() hotel
        @Prop({default: false, type: Boolean}) disableRequiredRule
        @Prop({default: false}) _valid

        sameContactDataForAll = true
        formRefs = []

        @Emit()
        changeValid(val) {
            return val
        }

        @Watch('tourist.email', {immediate: true})
        onEmailChange(val) {
            if (this.index === 0 && this.roomLength > 1 && this.sameContactDataForAll) {
                this.setSameContactDataForAll('email', val)
            }
        }

        @Watch('tourist.phone', {immediate: true})
        onPhoneChange(val) {
            if (this.index === 0 && this.roomLength > 1 && this.sameContactDataForAll) {
                this.setSameContactDataForAll('phone', val)
            }
        }

        mounted() {
            EventBus.$on(SAME_CONTACT_DATA_FOR_ALL, this.setSameContactData)
        }

        beforeUnmount() {
            EventBus.$off(SAME_CONTACT_DATA_FOR_ALL, this.setSameContactData)
            if (this.showMealTypes) {
                this.setProp('alternativeMealTypeId', null)
            }
        }

        personSelected({person}) {
            const index = this.index,
                roomIndex = this.roomIndex
            if (person) {
                const val = {
                    ...hotelsStore.tourists[roomIndex][index],
                    ...runtimeStore.touristFromPerson(person),
                    ...(person.personId && {personId: person.personId}),
                }
                if (!person.personId) delete val.personId
                hotelsStore.SET_TOURIST({roomIndex, index, val})
            } else {
                hotelsStore.SET_TOURIST_PROP({roomIndex, index, prop: 'personId', val: null})
            }
        }

        setSameContactDataForAll(prop, val) {
            const allRooms = hotelsStore.tourists.length
            for (let i = 0; i < allRooms; i++) {
                hotelsStore.SET_TOURIST_PROP({prop, val, roomIndex: i, index: 0})
            }
        }

        changeSameContactData(value) {
            EventBus.$emit(SAME_CONTACT_DATA_FOR_ALL, value)
            this.setSameContactDataForAll('email', hotelsStore.tourists[0][0].email)
            this.setSameContactDataForAll('phone', hotelsStore.tourists[0][0].phone)
        }

        setSameContactData(value) {
            this.sameContactDataForAll = value
        }

        getProp(prop) {
            return hotelsStore.tourists[this.roomIndex][this.index][prop]
        }

        setProp(prop, val) {
            const roomIndex = this.roomIndex,
                index = this.index
            hotelsStore.SET_TOURIST_PROP({prop, val, roomIndex, index})
        }

        get roomLength() {
            return hotelsStore.tourists.length
        }

        get selectedTourists() {
            return hotelsStore.tourists.reduce(
                (selectedTourists, roomTourists) => [...selectedTourists, ...roomTourists],
                []
            )
        }

        get isTourLead() {
            return this.roomIndex === 0 && this.index === 0
        }

        get defaultMealType() {
            return hotelsStore.extraMealTypes[this.roomIndex]?.find(({name}) => name === this.standardMealName)
        }

        get alternativeMealTypeId() {
            return this.getProp('alternativeMealTypeId') || this.defaultMealType?.id
        }

        set alternativeMealTypeId(value) {
            this.setProp('alternativeMealTypeId', value)
        }

        get lockEditPrivateClientName() {
            return this.$conf.public.lockEditPrivateClientName && this.index === 0 && this.roomIndex === 0
        }

        get showSameContactDataCheckbox() {
            return (
                this.isTourLead &&
                this.roomLength > 1 &&
                (!this.isFieldDisabled('PHONE') || !this.isFieldDisabled('EMAIL'))
            )
        }

        get disableCitizenship() {
            return false
        }

        get countryId() {
            return hotelsRuntimeStore.city.countryId
        }

        get bedType() {
            if (this.tourist.onExtrabed) return 'onExtraBed'
            else if (this.tourist.onWithoutPlace) return 'onWithoutPlace'

            return null
        }

        get valid() {
            return this._valid
        }

        set valid(val) {
            this.changeValid(val)
        }
    }

    export default HotelTouristForm
</script>
